import type { NoteSource, RecordEntity } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { api } from '../../lib/client.ts';
import { NoteCreateDialog } from '../notes/note-create-dialog.tsx';
import {
  NoteOriginFilter,
  pruneNoteFilter,
  type NoteFilter,
} from '../notes/note-origin-filter.tsx';
import type { TaskKind } from '../tasks/task-kind-select.tsx';
import { BLOCK_LIST_CLASS, BlockCard } from './block-card.tsx';
import { NotesSection, TasksSection, notesQuery } from './record-sidebar.tsx';

/**
 * A record's notes and tasks as two cards side by side, notes left at 60% and tasks right at 40%
 * (a container query: the content's width, so it also works in the candidate side panel; stacked
 * when narrow). Each list stops at about three collapsed notes and scrolls; "Full screen" opens
 * the card alone in a dialog, so the notes drop their per-note Maximize button. Shared by the
 * project and candidate Overview tabs. The project passes:
 * - `sources` (`GET /notes/sources`): the notes of its company, contacts, deals, jobs and
 *   shortlisted candidates are listed too, filtered by `NoteOriginFilter` beside "Full screen"
 *   (a toggle per kind, several at once, each narrowed to some records), and notes are added
 *   through "Add note" (pick the record in a tree, then write in a dialog) instead of the
 *   inline form;
 * - `kindFilter`: the Manual / Automated / All select on the tasks, Manual first.
 * The full-screen cards share the filter's and the select's choice.
 */
export function NotesTasksBlock({
  entityType,
  entityId,
  canWrite,
  sources,
  kindFilter = false,
}: {
  entityType: RecordEntity;
  entityId: string;
  canWrite: boolean;
  sources?: readonly NoteSource[] | undefined;
  kindFilter?: boolean;
}) {
  const [kind, setKind] = useState<TaskKind>(kindFilter ? 'manual' : 'all');
  // The same query (and cache entry) as the list below, for the filter's note counts.
  const notes = useQuery({
    queryKey: [...notesQuery(entityId, entityType), 'related'],
    queryFn: () => api.notes.list(entityType, entityId, { includeRelated: true }),
    enabled: sources !== undefined,
  });
  // The record's own notes to start with, the related ones a toggle away; but a record with
  // none of its own starts on every kind (a project's imported notes are all on its deals, jobs
  // and contacts, so it would open empty). Decided once, when the notes first load: a note added
  // later does not flip it, and nothing does once a toggle is touched.
  const own = useMemo<NoteFilter>(() => ({ [entityType]: 'all' }), [entityType]);
  const [touched, setTouched] = useState<NoteFilter | null>(null);
  const [start, setStart] = useState<NoteFilter | null>(null);
  if (start === null && sources?.length && notes.data) {
    const ownNotes = notes.data.some((n) => n.entityType === entityType && n.entityId === entityId);
    setStart(
      ownNotes
        ? own
        : Object.fromEntries(
            [...new Set(sources.map((src) => src.entityType))].map((k) => [k, 'all']),
          ),
    );
  }
  const chosenFilter = touched ?? start ?? own;
  const setFilter = useCallback(
    (action: SetStateAction<NoteFilter>) =>
      setTouched((prev) => (typeof action === 'function' ? action(prev ?? start ?? own) : action)),
    [start, own],
  );
  const filter = useMemo(
    // Not while the sources load (none yet): every kind would go, and every note show.
    () => (sources?.length ? pruneNoteFilter(chosenFilter, sources) : chosenFilter),
    [chosenFilter, sources],
  );
  const counts = useMemo(() => {
    const byRecord = new Map<string, number>();
    for (const n of notes.data ?? []) byRecord.set(n.entityId, (byRecord.get(n.entityId) ?? 0) + 1);
    return byRecord;
  }, [notes.data]);
  const originFilter = sources && sources.length > 0 && (
    <NoteOriginFilter sources={sources} counts={counts} value={filter} onChange={setFilter} />
  );
  return (
    <div className="@container">
      <div
        className="grid gap-5 @3xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
        data-testid="overview-notes-tasks"
      >
        <BlockCard
          title="Notes"
          filter={originFilter}
          action={sources && <NoteCreateDialog sources={sources} />}
        >
          {(full) => (
            <NotesSection
              entityType={entityType}
              entityId={entityId}
              canWrite={canWrite}
              sources={sources}
              filter={filter}
              composer={!sources}
              openable={false}
              listClassName={full ? '' : BLOCK_LIST_CLASS}
            />
          )}
        </BlockCard>
        <BlockCard title="Tasks">
          {(full) => (
            <TasksSection
              entityType={entityType}
              entityId={entityId}
              canWrite={canWrite}
              listClassName={full ? '' : BLOCK_LIST_CLASS}
              kind={kind}
              onKindChange={kindFilter ? setKind : undefined}
            />
          )}
        </BlockCard>
      </div>
    </div>
  );
}
